import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import type { Api } from '../../lib/api';
import { Toast } from '../../ui';
import type { Inverse } from './model/actions';
import { useInvalidateContent } from './queries';

/** How long "Undo" stays offered (P16-4 Ruling 3). */
export const UNDO_MS = 6000;

type Undoable = (run: () => Promise<void>, inverses: Inverse[], message: string) => Promise<void>;
type Shown = { key: number; message: string; inverses: Inverse[] | null };

const UndoContext = createContext<{ undoable: Undoable; notify: (message: string) => void } | null>(null);

function apply(api: Api, inverse: Inverse): Promise<void> {
  switch (inverse.kind) {
    case 'move':
      return api.updateBoard(inverse.boardId, { projectId: inverse.projectId, folderId: inverse.folderId });
    case 'rename':
      return api.updateBoard(inverse.boardId, { title: inverse.title });
    case 'favorite':
      return api.setFavorite(inverse.board.id, inverse.on);
    case 'lifecycle':
      return api.lifecycle('board', inverse.action, inverse.boardId);
    case 'projectLifecycle':
      return api.lifecycle('project', inverse.action, inverse.projectId);
  }
}

/**
 * The dashboard's undo toast: an action runs, the lists refresh, "Undo" is offered for 6 s and applies the inverse
 * operations. A new action replaces the toast without undoing the previous one. A failing action shows nothing here:
 * its error goes back to the caller.
 */
export function UndoProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const api = useApi();
  const invalidate = useInvalidateContent();
  const [shown, setShown] = useState<Shown | null>(null);
  const show = (message: string, inverses: Inverse[] | null) => setShown((s) => ({ key: (s?.key ?? 0) + 1, message, inverses }));

  const undoable = useCallback<Undoable>(
    async (run, inverses, message) => {
      await run();
      await invalidate();
      show(message, inverses.length ? inverses : null);
    },
    // `invalidate` is rebuilt each render but only reaches the stable query client.
    [],
  );

  const undo = async (inverses: Inverse[]) => {
    setShown(null);
    const results = await Promise.allSettled(inverses.map((i) => apply(api, i)));
    await invalidate();
    show(results.some((r) => r.status === 'rejected') ? t('dashboard.undoFailed') : t('dashboard.undone'), null);
  };

  const value = useMemo(() => ({ undoable, notify: (message: string) => show(message, null) }), [undoable]);

  return (
    <UndoContext.Provider value={value}>
      {children}
      {shown && (
        <Toast
          key={shown.key}
          message={shown.message}
          duration={shown.inverses ? UNDO_MS : undefined}
          action={shown.inverses ? { label: t('dashboard.undo'), onClick: () => void undo(shown.inverses!) } : undefined}
          onDone={() => setShown((s) => (s?.key === shown.key ? null : s))}
        />
      )}
    </UndoContext.Provider>
  );
}

/** Outside the dashboard frame (a lone card in a test, the template gallery's dialogs) actions run without a toast. */
export function useUndoable(): Undoable {
  const value = useContext(UndoContext)?.undoable;
  const invalidate = useInvalidateContent();
  return value ?? (async (run) => {
    await run();
    await invalidate();
  });
}

/** A plain message in the dashboard's toast (a refused shortcut says why); nothing outside the frame. */
export function useNotify(): (message: string) => void {
  return useContext(UndoContext)?.notify ?? (() => {});
}
